import {createRouter, createWebHistory, RouteLocationNormalized, RouteRecordRaw} from 'vue-router';
import {useAppSession} from './core/app-session';
import {openPaddleCheckout} from './core/paddle';
import {getCheckoutOfferCode} from './components/pricing/pricing.logic';
import ChunkErrorFallback from './components/layout/ChunkErrorFallback.vue';
import {loadChunkWithRecovery} from './core/chunk-recovery';
import {installPageMetadata} from './core/page-metadata';
import {
    REFERRAL_LIFETIME_DISCOUNT_CODE,
    acceptStoredReferral,
    captureInvitationCode,
} from './components/referrals/referrals';

function lazyRoute(loader: () => Promise<any>) {
    return () =>
        loadChunkWithRecovery(loader, {
            fallback: {default: ChunkErrorFallback},
            storage: sessionStorage,
            reload: () => window.location.reload(),
            logger: console,
        });
}

// Lazy-load route components to enable route-level code splitting.
const Main = lazyRoute(() => import('./components/main/Main.vue'));
const NotFound = lazyRoute(() => import('./components/layout/views/NotFoundView.vue'));
const BuyNow = lazyRoute(() => import('./components/layout/views/BuyNowView.vue'));
const Login = lazyRoute(() => import('./components/layout/views/LoginView.vue'));
const LoginExpired = lazyRoute(() => import('./components/layout/views/LoginExpiredView.vue'));
const Pricing = lazyRoute(() => import('./components/pricing/Pricing.vue'));
const Success = lazyRoute(() => import('./components/layout/views/SuccessView.vue'));
const Upgrade = lazyRoute(() => import('./components/layout/views/UpgradeView.vue'));
const Redirect = lazyRoute(() => import('./components/layout/views/RedirectView.vue'));
const Gallery = lazyRoute(() => import('./components/gallery/Gallery.vue'));
const Projects = lazyRoute(() => import('./components/projects/Projects.vue'));
const Assets = lazyRoute(() => import('./components/assets/Assets.vue'));
const Careers = lazyRoute(() => import('./components/careers/Careers.vue'));
const CareerJobPage = lazyRoute(() => import('./components/careers/CareerJobPage.vue'));
const CanvasFlowEditor = lazyRoute(() => import('./components/layout/views/CanvasFlowView.vue'));
const GalleryEditor = lazyRoute(() => import('./components/gallery/GalleryEditor.vue'));

// import Prototype from './components/layout/views/PrototypeView.vue';

// Define the app routes with lazy-loaded components.
const routes = [
    {path: '/assets', component: Assets},
    // {path: '/prototype', component: Prototype},
    {path: '/gallery', component: Gallery},
    {path: '/careers', component: Careers},
    {path: '/careers/embedded-ui-graphics-engineer', component: CareerJobPage},
    {
        path: '/gallery/:project_id/:screen_id?',
        component: GalleryEditor,
    },
    {path: '/price', component: Pricing},
    {path: '/login', component: Login},
    {path: '/upgrade', component: Upgrade},
    {path: '/login-expired', component: LoginExpired},
    {path: '/redirect/:target(.*)', component: Redirect},
    {
        path: '/projects',
        component: Projects,
    },
    {
        path: '/',
        component: Main,
    },
    {
        path: '/editor/:project_id/:screen_id?',
        redirect: (to: RouteLocationNormalized) => ({
            path: to.path.replace(/^\/editor(?=\/)/, '/canvas'),
            query: to.query,
            hash: to.hash,
        }),
    },
    {
        path: '/canvas/:project_id/:screen_id?',
        component: CanvasFlowEditor,
    },
    {
        path: '/sandbox',
        redirect: '/',
    },
    {
        path: '/buy_plus',
        component: BuyNow,
    },
    {
        path: '/buy_plus_annual',
        redirect: (to: RouteLocationNormalized) => ({path: '/price', query: to.query}),
    },
    {
        path: '/buy_plus_lifetime',
        component: BuyNow,
    },
    {
        path: '/buy_plus_week',
        component: BuyNow,
    },
    {
        path: '/buy_hobby',
        component: BuyNow,
    },
    {
        path: '/buy_hobby_annual',
        component: BuyNow,
    },
    {
        path: '/success',
        component: Success,
    },
    {
        path: '/404',
        component: NotFound,
    },
    {
        path: '/:pathMatch(.*)*',
        component: NotFound,
    },
];

const router = createRouter({
    history: createWebHistory(),
    routes: routes as RouteRecordRaw[],
});

function getCheckoutPlan(path: string): 'plus' | 'hobby' {
    return path.includes('plus') ? 'plus' : 'hobby';
}

router.beforeEach(async (to) => {
    captureInvitationCode(to.fullPath.includes('?') ? `?${to.fullPath.split('?')[1].split('#')[0]}` : '');
    const appSession = useAppSession();
    await appSession.initialize();

    if (appSession.state.auth?.user?.id) {
        try {
            const {acceptReferral} = await import('./api/users');
            await acceptStoredReferral(localStorage, acceptReferral);
        } catch {
            // Keep an unassociated invitation in storage for a later navigation.
        }
    }

    if (to.path === '/' && appSession.state.auth) {
        return `/projects${window.location.hash}`;
    }

    if (to.path === '/login' && appSession.state.auth) {
        const isRecovery = to.query['mode'] === 'recovery' || to.query['type'] === 'recovery';
        if (!isRecovery) {
            const redirect = Array.isArray(to.query.redirect) ? to.query.redirect[0] : to.query.redirect;
            if (typeof redirect === 'string' && redirect.startsWith('/') && !redirect.startsWith('//')) {
                return redirect;
            }
            return '/projects';
        }
    }

    if (to.path === '/upgrade' && !appSession.state.auth) {
        return `/login?redirect=${encodeURIComponent(to.fullPath)}`;
    }

    if (to.path.startsWith('/buy_') && appSession.state.auth) {
        const {email, id} = appSession.state.auth.user;
        const billingCycle = to.path.includes('lifetime')
            ? 'lifetime'
            : to.path.includes('annual')
              ? 'annual'
              : to.path.includes('week')
                ? 'week'
                : 'month';
        let discountCode = getCheckoutOfferCode(to.query);
        if (billingCycle === 'lifetime') {
            try {
                const {getReferralSummary} = await import('./api/users');
                const summary = await getReferralSummary();
                if (summary.lifetime_discount_eligible) discountCode = REFERRAL_LIFETIME_DISCOUNT_CODE;
            } catch {
                // Checkout still works without the optional referral discount lookup.
            }
        }
        void openPaddleCheckout(email, id, getCheckoutPlan(to.path), billingCycle, discountCode);
    }

    return true;
});

// Runs after redirects and guards have settled so the head reflects the route
// that is actually rendered.
router.afterEach((to) => {
    installPageMetadata(document, to.path);
});

router.onError((error, to) => {
    const isStaleChunk =
        error.message.includes('Failed to fetch dynamically imported module') ||
        error.message.includes('Unable to preload');
    if (isStaleChunk && to?.fullPath) {
        window.location.href = to.fullPath;
    }
});

export default router;
